import { CustomModal } from "@/components/custom-modal";
import { Button } from "@/components/ui/button";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { deleteMarketSetting } from "@/services/Merchant.service";
import { toast } from "react-toastify";

type DeleteConfirmProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rateId: string;
  currencyPair: string;
  onSuccess?: () => void;
};

export default function DeleteConfirm({
  open,
  onOpenChange,
  rateId,
  currencyPair,
  onSuccess,
}: DeleteConfirmProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteMarketSetting(rateId);
      toast.success("Market rate deleted successfully");
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to delete market rate"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <CustomModal open={open} onOpenChange={onOpenChange} size="sm">
      <div className="px-6 py-6 flex flex-col items-center gap-4 text-center">
        <div className="h-12 w-12 rounded-full bg-red-100 flex items-center justify-center">
          <AlertTriangle className="h-6 w-6 text-red-600" />
        </div>

        <DialogHeader className="text-center">
          <DialogTitle className="text-lg font-semibold text-primary-3">
            Delete Market Rate
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-gray-70">
          Are you sure you want to delete the rate for{" "}
          <span className="font-semibold text-primary-3">{currencyPair}</span>?
          This action cannot be undone.
        </p>
      </div>

      <DialogFooter className="px-6 py-4 flex flex-row justify-end items-center gap-3 bg-off-white-1 rounded-b-[16px] border-t">
        <Button
          type="button"
          variant="outline"
          className="h-10 rounded-[8px] px-4"
          onClick={() => onOpenChange(false)}
          disabled={isDeleting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          className="h-10 rounded-[8px] px-4 bg-red-600 hover:bg-red-700 text-white"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          {isDeleting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            "Delete"
          )}
        </Button>
      </DialogFooter>
    </CustomModal>
  );
}
